import { readonly, ref } from 'vue';

import { useAuthStore } from '@/stores/auth';
import { useLocaleStore } from '@/stores/locales';

type AxeptioChoices = Record<string, unknown>;

interface AxeptioSdk {
  on: (event: 'cookies:complete', callback: (choices: AxeptioChoices) => void) => void;
}

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
    _axcb?: ((axeptio: AxeptioSdk) => void)[];
  }
}

/** Name of the Beamer vendor in the Axeptio project */
const AXEPTIO_BEAMER_VENDOR = 'beamer';

const hasBeamerConsent = ref(false);
let isListeningBeamerConsent = false;

function getBeamerConsentFromCookie(): boolean {
  const match = document.cookie.match(/(?:^|;\s*)axeptio_cookies=([^;]*)/);
  if (!match) return false;

  try {
    return JSON.parse(decodeURIComponent(match[1]))[AXEPTIO_BEAMER_VENDOR] === true;
  } catch {
    return false;
  }
}

/**
 * Whether the user consented to Beamer in Axeptio.
 * Initialized from the Axeptio cookie, then kept in sync each time the user updates their choices.
 */
export function useBeamerConsent() {
  if (!isListeningBeamerConsent) {
    isListeningBeamerConsent = true;
    hasBeamerConsent.value = getBeamerConsentFromCookie();

    window._axcb = window._axcb || [];
    window._axcb.push(axeptio => {
      axeptio.on('cookies:complete', choices => {
        hasBeamerConsent.value = choices[AXEPTIO_BEAMER_VENDOR] === true;
      });
    });
  }

  return readonly(hasBeamerConsent);
}

/**
 * Beamer is loaded by GTM (tag "Beamer - Widget"), only once both events have been pushed:
 * - `beamer_ready` (pushed here, with the user data)
 * - `axeptio_activate_beamer` (pushed by Axeptio when the user consents to Beamer)
 */
export function setupBeamerScript() {
  const authStore = useAuthStore();
  const user = authStore.getFullUser;

  const role = authStore.hasPermission('manager_r') ? 'manager' : 'recruiter';
  const competencyFramework = authStore.hasPermission('competency_framework_group_r') ? 'with_cf' : 'without_cf';
  const managerAccount = authStore.hasPermission('manager_a') ? 'with_ma' : 'without_ma';
  const voice = authStore.hasPermission('voice') ? 'with_voice' : 'without_voice';

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'beamer_ready',
    beamer: {
      language: useLocaleStore().getCurrentISO,
      force_filter: `${role};${authStore.getCompany.plan};${competencyFramework};${managerAccount};${voice}`,
      user_firstname: user.first_name,
      user_lastname: user.last_name,
      user_email: user.email,
      user_id: user.uuid
    }
  });
}
